/**
 * AdBuilder.jsx — WebsiteTech.pro visual ad composer
 * ─────────────────────────────────────────────────
 *
 * Route: `/ads/new` (admin-only). Renders:
 *  1. Ad copy inputs — headline, tagline, CTA label, landing URL.
 *  2. Hero image controls — describe → Nano Banana OR upload photo.
 *  3. Live preview — card + share-ready block.
 *  4. Publish → server generates short URL + QR code + landing.
 *
 * All state is client-side until Publish; the backend only stores
 * the temp image asset (until Publish confirms the final ad).
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Sparkles, Upload, Loader2, Wand2, QrCode, Link2,
  Share2, Copy, ExternalLink, Image as ImageIcon,
} from "lucide-react";

import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const API_ORIGIN =
  (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") ||
  "https://social-engine-12.preview.emergentagent.com";

function BusyOverlay({ label }) {
  return (
    <div className="absolute inset-0 grid place-items-center bg-white/70 backdrop-blur-sm rounded-2xl">
      <div className="flex items-center gap-2 text-slate-600 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> {label}
      </div>
    </div>
  );
}

export default function AdBuilder() {
  const { user, loading: authLoading } = useAuth();
  const [headline, setHeadline] = useState("Build a faster website");
  const [tagline, setTagline]   = useState("Everything you need to ship in a weekend — hosting, domains, templates.");
  const [ctaLabel, setCtaLabel] = useState("Get started");
  const [targetUrl, setTargetUrl] = useState("https://websitetech.pro/pricing");
  const [theme, setTheme] = useState("light");   // light | dark

  const [imagePrompt, setImagePrompt] = useState("bright modern startup workspace, laptop with a colorful web builder on screen, natural light");
  const [heroTempId, setHeroTempId] = useState(null);
  const [heroUrl, setHeroUrl] = useState(null);
  const [heroBusy, setHeroBusy] = useState(null);   // "generate" | "upload"

  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(null);

  const canPublish = useMemo(
    () => headline.trim() && tagline.trim() && targetUrl.trim().startsWith("https://") && heroTempId,
    [headline, tagline, targetUrl, heroTempId],
  );

  const generateImage = async () => {
    if (!imagePrompt.trim()) { toast.error("Describe the image first"); return; }
    setHeroBusy("generate");
    try {
      const r = await http.post("/websitetech/ads/generate-image", { prompt: imagePrompt.trim() });
      setHeroTempId(r.data.temp_id);
      setHeroUrl(r.data.image_url);
      toast.success("Hero image generated");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setHeroBusy(null); }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setHeroBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await http.post("/websitetech/ads/upload-photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setHeroTempId(r.data.temp_id);
      setHeroUrl(r.data.image_url);
      toast.success("Photo uploaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setHeroBusy(null); }
  };

  const publish = async () => {
    if (!canPublish) return;
    setPublishing(true);
    try {
      const r = await http.post("/websitetech/ads", {
        headline: headline.trim(),
        tagline: tagline.trim(),
        cta_label: ctaLabel.trim() || "Learn more",
        target_url: targetUrl.trim(),
        temp_image_id: heroTempId,
        theme,
      });
      setPublished(r.data);
      toast.success(`Ad published — short URL /s/${r.data.short_code}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setPublishing(false); }
  };

  const copy = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Clipboard blocked"),
    );
  };

  const shareOn = (network) => {
    if (!published) return;
    const url = encodeURIComponent(published.short_url);
    const text = encodeURIComponent(`${published.headline} — ${published.tagline}`);
    const map = {
      twitter:  `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      whatsapp: `https://api.whatsapp.com/send?text=${text}%20${url}`,
      email:    `mailto:?subject=${encodeURIComponent(published.headline)}&body=${text}%0A${url}`,
    };
    window.open(map[network], "_blank", "noopener,noreferrer");
  };

  useEffect(() => {
    // Prime the image on first render so admins see something.
    // No auto-generate — just wait for click.
  }, []);

  if (authLoading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!user) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 text-center px-6">
        <div>
          <p className="text-slate-700 font-serif text-2xl mb-3">Sign in required</p>
          <Link to="/join" className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold px-5 py-2 text-sm">Sign in →</Link>
        </div>
      </div>
    );
  }

  const previewCardCls = theme === "dark"
    ? "bg-slate-900 text-white ring-slate-800"
    : "bg-white text-slate-900 ring-slate-200";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <Link to="/admin/content-engine/report" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800 mb-4" data-testid="ad-builder-back">
          <ArrowLeft className="w-3 h-3" /> Back to Content Engine
        </Link>

        <header className="mb-6 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <h1 className="font-serif text-3xl font-bold text-slate-900">Ad Builder</h1>
            <p className="text-slate-500 text-sm mt-1">Compose a hero ad for <span className="font-mono text-slate-700">websitetech.pro</span> — AI image, tagline, short URL, QR.</p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={publish}
              disabled={!canPublish || publishing}
              data-testid="ad-builder-publish"
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-4 py-2"
            >
              {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {published ? "Republish" : "Publish ad"}
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* ── Left: form ── */}
          <div className="space-y-4">
            <section className="rounded-2xl bg-white ring-1 ring-slate-200 p-5">
              <h2 className="text-sm font-serif font-bold text-slate-900 mb-3">Copy</h2>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1">Headline</label>
              <input
                type="text"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                data-testid="ad-builder-headline"
                className="w-full rounded-lg ring-1 ring-slate-300 focus:ring-slate-500 outline-none px-3 py-2 text-sm mb-3"
                maxLength={120}
              />
              <label className="block text-[11px] font-semibold text-slate-500 mb-1">Tagline</label>
              <textarea
                rows={2}
                value={tagline}
                onChange={(e) => setTagline(e.target.value)}
                data-testid="ad-builder-tagline"
                className="w-full rounded-lg ring-1 ring-slate-300 focus:ring-slate-500 outline-none px-3 py-2 text-sm mb-3 resize-y"
                maxLength={200}
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">CTA label</label>
                  <input
                    type="text"
                    value={ctaLabel}
                    onChange={(e) => setCtaLabel(e.target.value)}
                    data-testid="ad-builder-cta-label"
                    className="w-full rounded-lg ring-1 ring-slate-300 focus:ring-slate-500 outline-none px-3 py-2 text-sm"
                    maxLength={40}
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-semibold text-slate-500 mb-1">Theme</label>
                  <select
                    value={theme}
                    onChange={(e) => setTheme(e.target.value)}
                    data-testid="ad-builder-theme"
                    className="w-full rounded-lg ring-1 ring-slate-300 focus:ring-slate-500 outline-none px-3 py-2 text-sm bg-white"
                  >
                    <option value="light">Light</option>
                    <option value="dark">Dark</option>
                  </select>
                </div>
              </div>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1 mt-3">Landing URL (must be https)</label>
              <input
                type="url"
                value={targetUrl}
                onChange={(e) => setTargetUrl(e.target.value)}
                data-testid="ad-builder-target-url"
                className="w-full rounded-lg ring-1 ring-slate-300 focus:ring-slate-500 outline-none px-3 py-2 text-sm font-mono"
                placeholder="https://websitetech.pro/pricing"
              />
            </section>

            <section className="rounded-2xl bg-white ring-1 ring-slate-200 p-5 relative">
              <h2 className="text-sm font-serif font-bold text-slate-900 mb-3">Hero image</h2>
              <label className="block text-[11px] font-semibold text-slate-500 mb-1">Describe the image (Nano Banana)</label>
              <textarea
                rows={2}
                value={imagePrompt}
                onChange={(e) => setImagePrompt(e.target.value)}
                data-testid="ad-builder-image-prompt"
                className="w-full rounded-lg ring-1 ring-slate-300 focus:ring-slate-500 outline-none px-3 py-2 text-sm mb-2 resize-y"
              />
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={generateImage}
                  disabled={heroBusy === "generate"}
                  data-testid="ad-builder-generate-image"
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-slate-800 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5"
                >
                  {heroBusy === "generate" ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                  Generate with AI
                </button>
                <span className="text-slate-400 text-[11px]">or</span>
                <label className="inline-flex items-center gap-1.5 rounded-full ring-1 ring-slate-300 hover:ring-slate-500 text-slate-700 text-xs font-semibold px-3 py-1.5 cursor-pointer" data-testid="ad-builder-upload-photo-label">
                  <Upload className="w-3 h-3" /> Upload photo
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => uploadImage(e.target.files?.[0])}
                    data-testid="ad-builder-upload-photo"
                    className="hidden"
                  />
                </label>
              </div>
              {heroBusy && <BusyOverlay label={heroBusy === "generate" ? "Nano Banana is drawing…" : "Uploading…"} />}
            </section>
          </div>

          {/* ── Right: preview + publish output ── */}
          <div className="space-y-4">
            <section className={`rounded-2xl ring-1 p-5 ${previewCardCls}`} data-testid="ad-builder-preview">
              <div className="text-[10px] font-bold uppercase tracking-widest opacity-60 mb-2">Preview</div>
              <div className="rounded-xl overflow-hidden ring-1 ring-black/5 aspect-[16/9] bg-slate-100 grid place-items-center">
                {heroUrl ? (
                  <img src={heroUrl} alt="" className="w-full h-full object-cover" data-testid="ad-builder-preview-image" />
                ) : (
                  <div className="text-slate-400 text-xs flex items-center gap-1">
                    <ImageIcon className="w-4 h-4" /> hero image will appear here
                  </div>
                )}
              </div>
              <h3 className="mt-4 font-serif text-2xl font-bold leading-tight" data-testid="ad-builder-preview-headline">{headline || "Your headline"}</h3>
              <p className={`mt-2 text-sm leading-snug ${theme === "dark" ? "text-slate-200" : "text-slate-600"}`} data-testid="ad-builder-preview-tagline">{tagline || "Your tagline goes here."}</p>
              <div className="mt-4 flex items-center gap-2">
                <a
                  href={targetUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`inline-flex items-center gap-1 rounded-full font-semibold px-4 py-2 text-sm ${theme === "dark" ? "bg-white text-slate-900 hover:bg-slate-100" : "bg-slate-900 text-white hover:bg-slate-800"}`}
                  data-testid="ad-builder-preview-cta"
                >
                  {ctaLabel || "Learn more"} <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </section>

            {published && (
              <section className="rounded-2xl bg-white ring-1 ring-emerald-200 p-5" data-testid="ad-builder-published">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-emerald-600" />
                  <h2 className="text-sm font-serif font-bold text-slate-900">Live and ready to share</h2>
                </div>
                <div className="grid grid-cols-[auto_1fr] gap-3 items-center">
                  <img src={published.qr_url} alt="QR" className="w-24 h-24 rounded-lg ring-1 ring-slate-200 bg-white" data-testid="ad-builder-qr" />
                  <div className="space-y-2 min-w-0">
                    <div className="flex items-center gap-2">
                      <Link2 className="w-3 h-3 text-slate-400 shrink-0" />
                      <code className="text-xs font-mono text-slate-800 truncate" data-testid="ad-builder-short-url">{published.short_url}</code>
                      <button type="button" onClick={() => copy(published.short_url, "Short URL")} className="rounded-full ring-1 ring-slate-300 hover:ring-slate-500 px-2 py-0.5 text-[10px] font-semibold text-slate-600" data-testid="ad-builder-copy-short">
                        <Copy className="w-3 h-3 inline" /> Copy
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <QrCode className="w-3 h-3 text-slate-400 shrink-0" />
                      <a href={published.qr_url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-slate-600 underline hover:text-slate-800">Download QR PNG</a>
                    </div>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {["twitter", "linkedin", "whatsapp", "email"].map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => shareOn(n)}
                          data-testid={`ad-builder-share-${n}`}
                          className="inline-flex items-center gap-1 rounded-full ring-1 ring-slate-300 hover:ring-slate-500 text-slate-700 text-[11px] font-semibold px-2.5 py-1"
                        >
                          <Share2 className="w-3 h-3" /> {n}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <p className="text-[10px] text-slate-400 mt-3">
                  Ad id: <code>{published.id}</code> · Landing URL: <a href={published.landing_url || `${API_ORIGIN}/ads/${published.id}`} className="underline">/ads/{published.id}</a>
                </p>
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
